import { registerDecorators as _registerDecorators, LightningElement, registerComponent as _registerComponent } from "lwc";
import _tmpl from "./input.html";
import { showRemainingCharactersInfo } from '~/modules/utilities/inputUtilities';
import { validateCampaignId, validateEmail, validateOrgId, validateUrl } from '~/modules/utilities/schemaUtilities';
const UNLOCALIZED_LABEL = '** MISSING I18N **';
class C360Input extends LightningElement {
  constructor(...args) {
    super(...args);
    this.identifier = '';
    this.typeData = '';
    this.name = '';
    this.label = '';
    this.overview = '';
    this.shouldHideLabel = false;
    this.badge = '';
    this.type = 'text';
    this.minLength = void 0;
    this.maxLength = void 0;
    this.maxValue = void 0;
    this.minValue = void 0;
    this.placeholder = '';
    this.value = '';
    this.labelledBy = '';
    this.disabled = false;
    this.required = false;
    this.requiredText = UNLOCALIZED_LABEL;
    this.invalidUrlError = UNLOCALIZED_LABEL;
    this.invalidEmailError = UNLOCALIZED_LABEL;
    this.invalidMaxValueError = UNLOCALIZED_LABEL;
    this.invalidMinValueError = UNLOCALIZED_LABEL;
    this.invalidOrgIdError = UNLOCALIZED_LABEL;
    this.invalidCampaignIdError = UNLOCALIZED_LABEL;
    this.showRemainingCharacters = false;
    this.forceShowError = false;
    this.requireSecureUrl = false;
    this.labelTypeStyle = 'body-2';
    this.helpInputMessage = '';
    this.showTooltip = false;
    this.tooltipMessage = '';
    this.changeCallback = void 0;
    this.getCharactersRemainingText = void 0;
    this.changeBlur = void 0;
    this.changeFocus = void 0;
    this._hasRendered = false;
    this._hasBlurred = false;
    this._currentCharacters = 0;
  }
  changeValue(value) {
    const input = this.template.querySelector('input');
    if (input) {
      input.value = value;
    }
  }
  get currentCharacters() {
    return this._currentCharacters;
  }
  set currentCharacters(value) {
    if (value && !isNaN(Number(value))) {
      this._currentCharacters = value;
    } else {
      this._currentCharacters = 0;
    }
  }
  get showLabel() {
    return !this.shouldHideLabel && this.label;
  }
  get remainingCharacters() {
    return this.maxLength - this.currentCharacters;
  }
  get remainingCharactersCSS() {
    return this.hasTooManyCharacters ? 'remaining slds-grid_align-end required-message' : 'remaining slds-grid_align-end';
  }
  get showHelpInputMessage() {
    return this.helpInputMessage;
  }
  get hasTooManyCharacters() {
    return this.maxLength && this.remainingCharacters < 0;
  }
  get showRequiredError() {
    return this.required && this.currentCharacters === 0 && (this.forceShowError || this._hasBlurred);
  }
  get showInvalidError() {
    return !this.showRequiredError && this.isInvalid() && (this.forceShowError || this._hasBlurred);
  }
  get invalidError() {
    if (this.type === 'uri') {
      return this.invalidUrlError;
    }
    if (this.type === 'email') {
      return this.invalidEmailError;
    }
    if (this.type === 'number' && this.maxValue && Number(this.value) > this.maxValue) {
      return this.invalidMaxValueError;
    }
    if (this.type === 'number' && this.minValue !== undefined && Number(this.value) < this.minValue) {
      return this.invalidMinValueError;
    }
    if (this.type === 'orgId') {
      return this.invalidOrgIdError;
    }
    if (this.type === 'campaignId') {
      return this.invalidCampaignIdError;
    }
    return '';
  }
  get showRemainingCharactersInfo() {
    return (this.hasTooManyCharacters || this.showRemainingCharacters) && showRemainingCharactersInfo(this.currentCharacters, this.maxLength);
  }
  get charactersRemainingDescription() {
    if (this.getCharactersRemainingText) {
      return this.getCharactersRemainingText(this.remainingCharacters);
    }
    return `${this.remainingCharacters} characters remaining`;
  }
  get requiredCss() {
    if (this.required) {
      return 'required';
    }
    return '';
  }
  get inputCss() {
    if (this.showRequiredError || this.showInvalidError || this.hasTooManyCharacters) {
      return 'error slds-input';
    }
    return 'slds-input';
  }
  connectedCallback() {
    if (!this.label && !(this.labelledBy || this.ariaLabelledBy)) {
      console.error('<c360-input> was created without label or labelled-by attributes, this is an accessibility issue. All inputs should have a label associated with them.');
    }
    this.currentCharacters = typeof this.value === 'string' ? this.value.length : 0;
  }
  renderedCallback() {
    if (!this._hasRendered) {
      const input = this.template.querySelector('input');
      if (input) {
        var _querySelector$getAtt, _querySelector, _this$template;
        input.value = this.value;
        input.setAttribute('aria-labelledby', this.label ? (_querySelector$getAtt = (_querySelector = this.template.querySelector('label')) === null || _querySelector === void 0 ? void 0 : _querySelector.getAttribute('id')) !== null && _querySelector$getAtt !== void 0 ? _querySelector$getAtt : 'inputLabel' : this.labelledBy || this.ariaLabelledBy || '');
        this._hasRendered = true;

        // Validate number fields with explicit check for forceShowError
        if (!this.forceShowError || this.type !== 'number') return;
        const value = Number(this.value);
        const exceedsMax = this.maxValue && value > this.maxValue;
        const belowMin = this.minValue !== undefined && value < this.minValue;
        if (!exceedsMax && !belowMin) return;

        // Force blur state
        this._hasBlurred = true;

        // Add error class to input
        input.classList.add('error');

        // Show error message
        const errorContainer = (_this$template = this.template) === null || _this$template === void 0 ? void 0 : _this$template.querySelector('.required-message');
        if (errorContainer && !errorContainer.querySelector('span')) {
          const errorSpan = document.createElement('span');
          errorSpan.textContent = exceedsMax ? this.invalidMaxValueError : this.invalidMinValueError;
          errorContainer.appendChild(errorSpan);
        }
      }
    }
  }
  isInvalid() {
    // If we're empty and not required, then we're valid
    if (this.currentCharacters === 0 && !this.required) {
      return false;
    }
    if (this.type === 'email') {
      return !validateEmail(this.value);
    } else if (this.type === 'uri') {
      if (this.requireSecureUrl) {
        return this.value.toLocaleLowerCase().indexOf('https') === -1 || !validateUrl(this.value);
      }
      return !validateUrl(this.value);
    }
    if (this.type === 'number' && this.maxValue && Number(this.value) > this.maxValue) {
      return true;
    }
    if (this.type === 'number' && this.minValue !== undefined && Number(this.value) < this.minValue) {
      return true;
    }
    if (this.type === 'orgId') {
      return !validateOrgId(this.value);
    }
    if (this.type === 'campaignId') {
      return !validateCampaignId(this.value);
    }
    // If we get down here, it means we're plain text, so we're valid if we're not required, or not empty
    return this.required && this.currentCharacters === 0;
  }
  onChanged(event) {
    if (this.changeCallback) {
      this.changeCallback(event.target.value, event);
    }
    if (!this.placeholder || this.placeholder !== event.target.value) {
      this.currentCharacters = event.target.value.length;
    }
  }
  onFocus(event) {
    this._hasBlurred = false;
    event.preventDefault();
    if (this.changeFocus) {
      this.changeFocus();
    }
  }
  onBlur(event) {
    this._hasBlurred = true;
    event.preventDefault();
    if (this.changeBlur) {
      this.changeBlur();
    }
  }
  /*LWC compiler v7.1.3*/
}
C360Input.delegatesFocus = true;
_registerDecorators(C360Input, {
  publicProps: {
    identifier: {
      config: 0
    },
    typeData: {
      config: 0
    },
    name: {
      config: 0
    },
    label: {
      config: 0
    },
    overview: {
      config: 0
    },
    shouldHideLabel: {
      config: 0
    },
    badge: {
      config: 0
    },
    type: {
      config: 0
    },
    minLength: {
      config: 0
    },
    maxLength: {
      config: 0
    },
    maxValue: {
      config: 0
    },
    minValue: {
      config: 0
    },
    placeholder: {
      config: 0
    },
    value: {
      config: 0
    },
    labelledBy: {
      config: 0
    },
    disabled: {
      config: 0
    },
    required: {
      config: 0
    },
    requiredText: {
      config: 0
    },
    invalidUrlError: {
      config: 0
    },
    invalidEmailError: {
      config: 0
    },
    invalidMaxValueError: {
      config: 0
    },
    invalidMinValueError: {
      config: 0
    },
    invalidOrgIdError: {
      config: 0
    },
    invalidCampaignIdError: {
      config: 0
    },
    showRemainingCharacters: {
      config: 0
    },
    forceShowError: {
      config: 0
    },
    requireSecureUrl: {
      config: 0
    },
    labelTypeStyle: {
      config: 0
    },
    helpInputMessage: {
      config: 0
    },
    showTooltip: {
      config: 0
    },
    tooltipMessage: {
      config: 0
    },
    changeCallback: {
      config: 0
    },
    getCharactersRemainingText: {
      config: 0
    },
    changeBlur: {
      config: 0
    },
    changeFocus: {
      config: 0
    }
  },
  publicMethods: ["changeValue", "isInvalid"],
  fields: ["_hasRendered", "_hasBlurred", "_currentCharacters"]
});
const __lwc_component_class_internal = _registerComponent(C360Input, {
  tmpl: _tmpl,
  sel: "c360-input",
  apiVersion: 62
});
export default __lwc_component_class_internal;